import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaFileMedical, FaSearch, FaPlus, FaEdit, FaTrash, FaUserMd, FaCalendarAlt, FaTag, FaArrowLeft } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp, useAutoRefresh, triggerRefresh } from '../context/AppContext';
import FileAttachment from '../components/FileAttachment';
import RecordCard from '../components/RecordCard';
import { getEMRRecords, createEMRRecord, updateEMRRecord, deleteEMRRecord, getPatients, getDoctors, getFileUrl, MAX_ATTACHMENT_SIZE, MAX_ATTACHMENT_SIZE_MESSAGE } from '../services/api';

const EMPTY_FORM = { patientName: '', patientId: '', doctorId: '', title: '', content: '', category: '', date: new Date().toISOString().split('T')[0] };

const EMR = () => {
  const { showToast, user } = useApp();
  const role = user?.role || 'Doctor';
  const [records, setRecords] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showViewModal, setShowViewModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const fileRef = useRef();

  const buildFormData = (data) => {
    const fd = new FormData();
    Object.entries(data).forEach(([k, v]) => fd.append(k, v ?? ''));
    if (editId) fd.append('existingAttachments', JSON.stringify(existingAttachments));
    selectedFiles.forEach(f => fd.append('files', f));
    return fd;
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [emrRes, patRes, docRes] = await Promise.allSettled([
        getEMRRecords(search ? { search } : {}),
        role !== 'Patient' ? getPatients() : Promise.resolve({ data: { data: [] } }),
        role !== 'Doctor'  ? getDoctors() : Promise.resolve({ data: { data: [] } }),
      ]);
      setRecords(emrRes.status === 'fulfilled' ? (emrRes.value.data?.data || []) : []);
      if (patRes.status === 'fulfilled') setPatients(patRes.value.data?.data || []);
      if (docRes.status === 'fulfilled') setDoctors(docRes.value.data?.data || []);
    } catch { setRecords([]); }
    setLoading(false);
  }, [search, role]);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  useAutoRefresh(fetchAll, 'emr');

  const getPatientPhoto = (rec) => {
    if (role === 'Patient') return getFileUrl(user?.profileImage) || null;
    const p = patients.find(pt => String(pt.userId) === String(rec.patientId) || String(pt.id) === String(rec.patientId));
    return getFileUrl(p?.account?.profileImage) || null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = (selectedFiles.length > 0 || editId) ? buildFormData(form) : form;
      if (editId) {
        const res = await updateEMRRecord(editId, payload);
        setRecords(prev => prev.map(r => r.id === editId ? (res.data?.data || r) : r));
        showToast('EMR updated!', 'success');
      } else {
        const res = await createEMRRecord(payload);
        setRecords(prev => [res.data?.data, ...prev]);
        showToast('EMR record added!', 'success');
        triggerRefresh('emr');
      }
      setShowForm(false); setForm(EMPTY_FORM); setEditId(null); setSelectedFiles([]); setExistingAttachments([]);
    } catch (err) { showToast(err.message || 'Failed to save', 'error'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this EMR record?')) return;
    try {
      await deleteEMRRecord(id);
      setRecords(prev => prev.filter(r => r.id !== id));
      showToast('Deleted');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleView = (rec) => {
    setSelected(rec);
    setShowViewModal(true);
  };

  if (showForm) {
    return (
      <div className="page-wrapper">
        <Header title={editId ? 'Edit EMR Record' : 'Add EMR Record'} />
        <Sidebar />
        <main className="page-content">
          <div className="page-header">
            <div>
              <h1><FaFileMedical style={{ color: 'var(--primary)', marginRight: 8 }} />{editId ? 'Edit EMR Record' : 'Add EMR Record'}</h1>
              <p>{editId ? 'Update medical record' : 'Create a new electronic medical record'}</p>
            </div>
            <button className="btn btn-outline" onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); }}>
              <FaArrowLeft /> Back
            </button>
          </div>
          <div className="card" style={{ padding: 24, maxWidth: 700, margin: '0 auto' }}>
            <form onSubmit={handleSubmit}>
              <div className="form-group"><label>Title *</label><input required value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Initial Consultation" /></div>
              <div className="form-group">
                <label>Patient</label>
                <select value={form.patientId} onChange={e => {
                  const pObj = patients.find(pt => String(pt.userId) === e.target.value || String(pt.id) === e.target.value);
                  const pid = pObj?.userId ? String(pObj.userId) : e.target.value;
                  setForm(prev => ({ ...prev, patientId: pid, patientName: pObj ? pObj.name : '', doctorId: pObj?.doctorId ? String(pObj.doctorId) : '' }));
                }}>
                  <option value="">Select patient...</option>
                  {patients.map(p => <option key={p.id} value={p.userId || p.id}>{p.name} {p.userId ? '(Registered)' : ''}</option>)}
                </select>
              </div>
              {role === 'Admin' && doctors.length > 0 && (() => {
                const selectedPatient = patients.find(pt => String(pt.userId) === form.patientId || String(pt.id) === form.patientId);
                const assignedDoctors = selectedPatient?.doctorId
                  ? doctors.filter(d => String(d.id) === String(selectedPatient.doctorId))
                  : doctors;
                return (
                  <div className="form-group">
                    <label>Doctor</label>
                    <select value={form.doctorId} onChange={e => setForm(p => ({ ...p, doctorId: e.target.value }))}>
                      <option value="">Select doctor...</option>
                      {assignedDoctors.map(d => <option key={d.id} value={d.id}>{d.name}{d.specialization ? ` — ${d.specialization}` : ''}</option>)}
                    </select>
                    {selectedPatient?.doctorId && (
                      <div style={{ fontSize: 12, color: 'var(--text-light)', marginTop: 4 }}>Only this patient's assigned doctor can be selected.</div>
                    )}
                  </div>
                );
              })()}
              <div className="form-grid">
                <div className="form-group"><label>Date</label><input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} /></div>
                <div className="form-group"><label>Category</label><input value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))} placeholder="e.g. Diagnosis" /></div>
              </div>
              <div className="form-group"><label>Content / Notes</label><textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} placeholder="Medical notes, diagnosis, treatment plan..." style={{ minHeight: 100 }} /></div>
              <div className="form-group">
                <label>Attachment (PDF, Image, Word)</label>
                {existingAttachments.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                    {existingAttachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} compact={true}
                        onRemove={() => setExistingAttachments(prev => prev.filter((_, idx) => idx !== i))} />
                    ))}
                  </div>
                )}
                <input ref={fileRef} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={e => {
                  const files = Array.from(e.target.files);
                  if (files.some(f => f.size > MAX_ATTACHMENT_SIZE)) {
                    showToast(MAX_ATTACHMENT_SIZE_MESSAGE, 'error');
                    e.target.value = '';
                    return;
                  }
                  setSelectedFiles(files);
                }} style={{ padding: '8px 0', fontSize: 13 }} />
                {selectedFiles.length > 0 && <div style={{ fontSize: 12, color: 'var(--primary)', marginTop: 4 }}>{selectedFiles.map(f => f.name).join(', ')}</div>}
              </div>
              <div style={{ display: 'flex', gap: 12 }}>
                <button type="button" className="btn btn-outline" style={{ flex: 1 }} onClick={() => { setShowForm(false); setForm(EMPTY_FORM); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); }}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Add Record'}</button>
              </div>
            </form>
          </div>
        </main>
        <BottomNav />
      </div>
    );
  }

  const filteredRecords = records.filter(rec => !filterDate || rec.date === filterDate);

  return (
    <div className="page-wrapper">
      <Header title="EMR" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div><h1><FaFileMedical style={{ color: 'var(--primary)', marginRight: 8 }} />EMR Records</h1><p>{filteredRecords.length} records</p></div>
          {(role === 'Doctor' || role === 'Admin') && (
            <button className="btn btn-primary" onClick={() => { setForm(EMPTY_FORM); setEditId(null); setSelectedFiles([]); setExistingAttachments([]); setShowForm(true); }}><FaPlus /> Add</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
          <div className="search-bar" style={{ flex: 1, minWidth: 180, marginBottom: 0 }}>
            <FaSearch /><input placeholder="Search records..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <input
            type="date"
            value={filterDate}
            onChange={e => setFilterDate(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 13 }}
            title="Filter by date"
          />
          {filterDate && (
            <button className="btn btn-outline" style={{ padding: '8px 12px', fontSize: 12 }} onClick={() => setFilterDate('')}>
              Clear Filter
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : filteredRecords.length === 0 ? (
          <div className="empty-state"><FaFileMedical className="empty-state-icon" /><h3>No EMR records</h3></div>
        ) : (
          <div className="record-grid">
            {filteredRecords.map(rec => {
              const doctorName = rec.doctorName || (rec.doctorId ? (
                (role === 'Doctor' && rec.doctorId === user?.id) ? user.name :
                doctors.find(d => d.id === rec.doctorId)?.name || `Dr. #${rec.doctorId}`
              ) : null);
              const meta = [{ icon: <FaCalendarAlt size={11} />, text: rec.date ? new Date(rec.date).toLocaleDateString('en-IN') : '-' }];
              if (doctorName) meta.push({ icon: <FaUserMd size={11} />, text: doctorName });
              return (
                <RecordCard
                  key={rec.id}
                  avatarLetter={(rec.patientName || rec.title || '?')[0].toUpperCase()}
                  avatarImage={getPatientPhoto(rec)}
                  title={rec.patientName || 'General Record'}
                  meta={meta}
                  items={[{
                    icon: <FaFileMedical size={13} />,
                    name: rec.title,
                    badges: [rec.category ? <><FaTag size={9} style={{ marginRight: 4 }} />{rec.category}</> : 'General'],
                  }]}
                  note={rec.content ? (rec.content.length > 120 ? rec.content.slice(0, 120) + '…' : rec.content) : null}
                  noteLabel="Notes"
                  onView={() => handleView(rec)}
                  actions={(role === 'Doctor' || role === 'Admin') && (
                    <>
                      <button className="action-btn action-btn-edit" onClick={() => {
                        setForm({ patientName: rec.patientName || '', patientId: rec.patientId || '', doctorId: rec.doctorId || '', title: rec.title, content: rec.content || '', category: rec.category || '', date: rec.date });
                        setSelectedFiles([]);
                        setExistingAttachments(Array.isArray(rec.attachments) && rec.attachments.length > 0 ? rec.attachments : (rec.file_url ? [{ url: rec.file_url, name: null }] : []));
                        setEditId(rec.id); setShowForm(true);
                      }}><FaEdit /></button>
                      <button className="action-btn action-btn-delete" onClick={() => handleDelete(rec.id)}><FaTrash /></button>
                    </>
                  )}
                />
              );
            })}
          </div>
        )}
      </main>
      <BottomNav />

      {showViewModal && selected && (
        <div className="side-panel-overlay" onClick={e => e.target === e.currentTarget && setShowViewModal(false)}>
          <div className="side-panel">
            <div className="modal-header">
              <h2>EMR Record Details</h2>
              <button className="modal-close" onClick={() => setShowViewModal(false)}>×</button>
            </div>
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 15, paddingBottom: 10, borderBottom: '1px solid #eee' }}>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Patient</div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--primary)' }}>{selected.patientName || 'Unknown Patient'}</div>
                  <div style={{ fontSize: 13, color: 'var(--text-medium)', marginTop: 4 }}>Dr. {selected.doctorName || '-'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1 }}>Date & Category</div>
                  <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 4 }}>{selected.date ? new Date(selected.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}</div>
                  <span style={{ fontSize: 12, background: '#e3f2fd', color: '#1976d2', padding: '3px 8px', borderRadius: 10, fontWeight: 700 }}>{selected.category || 'General'}</span>
                </div>
              </div>

              <div style={{ marginBottom: 15 }}>
                <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Record Title</div>
                <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 10, borderLeft: '4px solid var(--primary)' }}>
                  <div style={{ fontWeight: 700, fontSize: 16 }}>{selected.title}</div>
                </div>
              </div>

              {selected.content && (
                <div style={{ marginBottom: 15 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 5 }}>Clinical Notes</div>
                  <div style={{ background: '#fff9c4', padding: 12, borderRadius: 8, fontSize: 14, color: '#5d4037', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{selected.content}</div>
                </div>
              )}

              {((Array.isArray(selected.attachments) && selected.attachments.length > 0) || selected.file_url) && (
                <div>
                  <div style={{ fontSize: 12, color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 8 }}>Attachments</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {Array.isArray(selected.attachments) && selected.attachments.map((att, i) => (
                      <FileAttachment key={i} url={att.url} filename={att.name} />
                    ))}
                    {!Array.isArray(selected.attachments) && selected.file_url && <FileAttachment url={selected.file_url} />}
                  </div>
                </div>
              )}
            </div>
            <button className="btn btn-primary" style={{ width: '100%' }} onClick={() => setShowViewModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EMR;
